/**
 * 匿名屋台評価の端末識別サービス
 * 端末内だけに保存する無作為トークンと、送信操作用IDを生成します。
 */

import webStorage from '../../../shared/services/webStorage';
import { AppError } from '../../../shared/utils/errorHandling';

/** 匿名屋台評価の端末トークン保存キー */
export const ANONYMOUS_STALL_RATING_DEVICE_TOKEN_STORAGE_KEY =
  'ikomasai.stallRating.anonymousDeviceToken.v1';

/** 端末トークン形式不正エラーコード */
export const ANONYMOUS_STALL_RATING_DEVICE_TOKEN_FAILED_CODE =
  'ANONYMOUS_STALL_RATING_DEVICE_TOKEN_FAILED';

/** 64桁lowercase hexadecimal形式 */
const DEVICE_TOKEN_PATTERN = /^[0-9a-f]{64}$/;

/** UUID形式 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * 端末トークン形式が正しいか判定する
 * @param {unknown} value - 判定対象
 * @returns {boolean} 64桁lowercase hexadecimalの場合true
 */
export const isValidAnonymousStallRatingDeviceToken = (value: unknown) => (
  DEVICE_TOKEN_PATTERN.test(String(value ?? ''))
);

/**
 * 暗号学的乱数から端末トークンを生成する
 * @returns {string} 64桁lowercase hexadecimal端末トークン
 * @throws {AppError} Web Crypto APIを利用できない場合
 */
const createDeviceToken = () => {
  if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') {
    throw new AppError('このブラウザでは匿名回答用の端末情報を準備できません。', {
      code: ANONYMOUS_STALL_RATING_DEVICE_TOKEN_FAILED_CODE,
      statusCode: 500,
    });
  }

  /** 256bitの暗号学的乱数 */
  const randomBytes = crypto.getRandomValues(new Uint8Array(32));
  return Array.from(randomBytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * 保存済み端末トークンを読み込み、未保存・破損時は新規生成する
 * @returns {Promise<string>} 匿名回答用端末トークン
 * @throws {AppError} 生成または保存に失敗した場合
 */
export const loadOrCreateAnonymousStallRatingDeviceToken = async () => {
  try {
    /** 端末内に保存済みのトークン */
    const storedToken = await webStorage.getItem(
      ANONYMOUS_STALL_RATING_DEVICE_TOKEN_STORAGE_KEY,
    );
    if (isValidAnonymousStallRatingDeviceToken(storedToken)) {
      return String(storedToken);
    }

    /** 新規生成した端末トークン */
    const generatedToken = createDeviceToken();
    await webStorage.setItem(
      ANONYMOUS_STALL_RATING_DEVICE_TOKEN_STORAGE_KEY,
      generatedToken,
    );
    return generatedToken;
  } catch (error) {
    if (
      error instanceof AppError
      && error.code === ANONYMOUS_STALL_RATING_DEVICE_TOKEN_FAILED_CODE
    ) {
      throw error;
    }
    throw new AppError('匿名回答用の端末情報を準備できませんでした。', {
      code: ANONYMOUS_STALL_RATING_DEVICE_TOKEN_FAILED_CODE,
      statusCode: 500,
      cause: error instanceof Error ? error : undefined,
    });
  }
};

/**
 * RPC操作の冪等性を担保するsubmission IDを生成する
 * @returns {string} UUID submission ID
 * @throws {AppError} Web Crypto APIを利用できない場合
 */
export const createAnonymousStallRatingSubmissionId = () => {
  if (typeof crypto === 'undefined' || typeof crypto.randomUUID !== 'function') {
    throw new AppError('匿名回答の送信準備に失敗しました。', {
      code: ANONYMOUS_STALL_RATING_DEVICE_TOKEN_FAILED_CODE,
      statusCode: 500,
    });
  }

  /** Web Crypto APIが生成したUUID */
  const submissionId = crypto.randomUUID();
  if (!UUID_PATTERN.test(submissionId)) {
    throw new AppError('匿名回答の送信準備に失敗しました。', {
      code: ANONYMOUS_STALL_RATING_DEVICE_TOKEN_FAILED_CODE,
      statusCode: 500,
    });
  }
  return submissionId;
};
